import { Col, Input, Row, Dropdown, Space, Menu } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { translate } from '@portal/services/i18n';
import DataTable from '@portal/components/DataTableStyled/DataTable';
import DetailDots from '@portal/assets/svg/manager-user/ic_detail_dots.svg?react';
import PlusIcon from '@portal/assets/svg/ic_plus.svg?react';

import { WL_COMPANY_PRIMARY_COLOR } from '@portal/config/env';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedSelect from '@portal/components/AdvancedSelect/AdvancedSelect';
import useReduxState from '@portal/hooks/useReduxState';
import { getOperators } from '@portal/actions/user';
import { useDispatch } from 'react-redux';
import { maskPhone } from '@portal/services/masks';
import { getRouteStackPath } from '@portal/config/routes';
import { formatDateWithHours } from '@portal/utils/format';
import Header from '@portal/components/Header/Header';
import { listAllGroups } from '@portal/actions/group';
import { OperatorStatus } from '@portal/enum/accountStatus';
import { useNavigation } from '@portal/hooks/useNavigation';

const menu = (
  <Menu>
    <Menu.Item>Editar</Menu.Item>
    <Menu.Item>Excluir</Menu.Item>
  </Menu>
);

const initialValues: advancedFilterModels.baseFilter = {
  orderBy: 'createdAt',
  page: 0,
  pageSize: 10,
  sort: 'asc',
};

const UserManagerList = () => {
  const [onFocusSearch, setOnFocusSearch] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [selectSearch, setSelectSearch] = useState<string>('');

  const [pageFilter, setPageFilter] =
    useState<advancedFilterModels.baseFilter>(initialValues);

  const LIMIT_FOR_PAGE = 5;

  const dispatch = useDispatch();

  const navigate = useNavigation();

  const {
    user: { myOperators, countOperators },
    group: { myGroups },
  } = useReduxState();

  useEffect(() => {
    if (searchText.length >= 3) {
      setTimeout(() => {
        dispatch(
          getOperators({
            limit: LIMIT_FOR_PAGE,
            offset: pageFilter.page,
            search: searchText.toLocaleLowerCase(),
            roleGroudId: '',
          })
        );
      }, 3000);
    }
  }, [searchText, dispatch, pageFilter.page]);

  useEffect(() => {
    dispatch(listAllGroups(100, 0, ''));
  }, [dispatch]);

  useEffect(() => {
    if (pageFilter.page > 0)
      dispatch(
        getOperators({
          limit: LIMIT_FOR_PAGE,
          offset: pageFilter.page,
          search: searchText.toLocaleLowerCase(),
          roleGroudId: '',
        })
      );
    else
      dispatch(
        getOperators({
          limit: LIMIT_FOR_PAGE,
          offset: 0,
          search: searchText.toLocaleLowerCase(),
          roleGroudId: '',
        })
      );
  }, [pageFilter, dispatch, searchText]);

  useEffect(() => {
    if (selectSearch) {
      dispatch(
        getOperators({
          limit: LIMIT_FOR_PAGE,
          offset: pageFilter.page,
          search: searchText.toLocaleLowerCase(),
          roleGroudId: selectSearch,
        })
      );
    }
  }, [pageFilter.page, searchText, selectSearch, dispatch]);

  const listGroups = useMemo(() => {
    return myGroups.map((item) => {
      return {
        ...item,
        value: item.id,
      };
    });
  }, [myGroups]);

  const listUser = useMemo(() => {
    if (myOperators && myOperators.length > 0)
      return myOperators.map((item) => {
        return {
          ...item,
          cellphone: maskPhone(item.cellphone ?? ''),
          lastLogin: item.lastLogin ? formatDateWithHours(item.lastLogin) : '-',
          profile: item.roleGroup?.name ?? '-',
        };
      });
  }, [myOperators]);

  const renderWithStatus = useCallback((value: OperatorStatus) => {
    switch (value) {
      case OperatorStatus.LINKED:
        return (
          <span className="manager-list__status-operator__linked">
            {translate(
              'PAGES.USER_MANAGER.USERS_SCREEN.OPERATOR_STATUS.' + value
            )}
          </span>
        );
      case OperatorStatus.AWAITING_LINKED:
        return (
          <span className="manager-list__status-operator__await-linking">
            {translate(
              'PAGES.USER_MANAGER.USERS_SCREEN.OPERATOR_STATUS.' + value
            )}
          </span>
        );
      case OperatorStatus.AWAITING_LOGIN:
        return (
          <span className="manager-list__status-operator__await-login">
            {translate(
              'PAGES.USER_MANAGER.USERS_SCREEN.OPERATOR_STATUS.' + value
            )}
          </span>
        );
      default:
        return '-';
    }
  }, []);

  return (
    <div>
      <Col className="manager-list">
        <Header subtitle={translate('PAGES.USER_MANAGER.USERS_SCREEN.TITLE')} />

        <Row>
          <label className="manager-list__search__label" htmlFor="input-search">
            <Input
              id="input-search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onFocus={() => setOnFocusSearch(true)}
              onBlur={() => setOnFocusSearch(false)}
              className="manager-list__search__input"
            />

            {!onFocusSearch && !searchText && (
              <span className="manager-list__search__input-placeholder">
                {translate(
                  'PAGES.USER_MANAGER.USERS_SCREEN.SEARCH.PLACEHOLDER_PART_ONE'
                )}
                <span className="manager-list__search__input-placeholder_color">
                  {' ' +
                    translate(
                      'PAGES.USER_MANAGER.USERS_SCREEN.SEARCH.PLACEHOLDER'
                    )}
                </span>
              </span>
            )}
          </label>

          <Col className="manager-list__search__select-type">
            <AdvancedSelect
              value={selectSearch}
              options={listGroups}
              label={translate(
                'PAGES.USER_MANAGER.USERS_SCREEN.SEARCH.GROUP_TYPE'
              )}
              onChange={(value) => setSelectSearch(value)}
            />
          </Col>

          <div style={{ flex: 1, height: 55 }}>
            <AdvancedButton
              height="3.438rem"
              startIcon={<PlusIcon style={{ marginLeft: '-30px' }} />}
              fullWidth={true}
              onClick={() =>
                navigate(getRouteStackPath('USER_MANAGER', 'NEW_USER'))
              }
              text={translate(
                'PAGES.USER_MANAGER.USERS_SCREEN.SEARCH.NEW_USER'
              )}
            />
          </div>
        </Row>

        <DataTable
          orderBy={initialValues.orderBy}
          sort={initialValues.sort}
          pageSize={LIMIT_FOR_PAGE}
          columns={[
            {
              field: 'name',
              flex: 1,
              headerName: translate(
                'PAGES.USER_MANAGER.USERS_SCREEN.TABLE.NAME'
              ),
              sortable: false,
              hide: false,
              renderHeader: () => (
                <div
                  style={{ color: WL_COMPANY_PRIMARY_COLOR }}
                  className="manager-list__header-text"
                >
                  Nome
                </div>
              ),
            },
            {
              field: 'email',
              flex: 1,
              sortable: false,
              hide: false,
              renderHeader: () => (
                <div
                  style={{ color: WL_COMPANY_PRIMARY_COLOR }}
                  className="manager-list__header-text"
                >
                  {translate('PAGES.USER_MANAGER.USERS_SCREEN.TABLE.EMAIL')}
                </div>
              ),
            },
            {
              field: 'cellphone',
              sortable: false,
              flex: 1,
              renderHeader: () => (
                <div
                  style={{ color: WL_COMPANY_PRIMARY_COLOR }}
                  className="manager-list__header-text"
                >
                  {translate('PAGES.USER_MANAGER.USERS_SCREEN.TABLE.PHONE')}
                </div>
              ),
            },
            {
              field: 'profile',
              sortable: false,
              hide: false,
              width: 150,
              renderHeader: () => (
                <div
                  style={{ color: WL_COMPANY_PRIMARY_COLOR }}
                  className="manager-list__header-text"
                >
                  {translate('PAGES.USER_MANAGER.USERS_SCREEN.TABLE.GROUP')}
                </div>
              ),
            },
            {
              field: 'operatorStatus',
              sortable: false,
              hide: false,
              width: 150,
              renderHeader: () => (
                <div
                  style={{ color: WL_COMPANY_PRIMARY_COLOR }}
                  className="manager-list__header-text"
                >
                  {translate('PAGES.USER_MANAGER.USERS_SCREEN.TABLE.STATUS')}
                </div>
              ),
              renderCell: (params) =>
                renderWithStatus(params.row?.operatorStatus),
            },
            {
              field: 'details',
              sortable: false,
              hide: false,
              renderHeader: () => (
                <div
                  style={{ color: WL_COMPANY_PRIMARY_COLOR }}
                  className="manager-list__header-text"
                >
                  {translate('PAGES.USER_MANAGER.USERS_SCREEN.TABLE.DETAILS')}
                </div>
              ),
              renderCell: () => (
                <Dropdown overlay={menu} trigger={['click']}>
                  <a onClick={(e) => e.preventDefault()}>
                    <Space>
                      <DetailDots />
                    </Space>
                  </a>
                </Dropdown>
              ),
            },
          ]}
          rows={listUser ?? []}
          rowCount={countOperators ?? LIMIT_FOR_PAGE}
          onChange={(info) => setPageFilter(info)}
        />
      </Col>
    </div>
  );
};

export default UserManagerList;
